// =============================================================================
// MeuDriver — Admin: pedidos de saque (payout_requests)
// -----------------------------------------------------------------------------
// Lista de solicitações pendentes com botões Aprovar / Rejeitar + observação.
// =============================================================================

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Wallet, Check, X, RefreshCw } from 'lucide-react'
import { formatCurrency, formatDateTime, getInitials } from '@/lib/utils'

interface PayoutRequest {
  id: string
  instructor_id: string
  amount: number
  status: 'pending' | 'processing' | 'paid' | 'rejected'
  requested_at: string
  resolved_at: string | null
  pix_key: string | null
  bank_info: string | null
  notes: string | null
  instructor: {
    id: string
    profile: { id: string; full_name: string | null; avatar_url: string | null } | null
  } | null
}

export function AdminPayoutRequestsCard() {
  const [requests, setRequests] = useState<PayoutRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'pending' | 'all'>('pending')

  const [actionDialog, setActionDialog] = useState<
    | { req: PayoutRequest; action: 'paid' | 'rejected' }
    | null
  >(null)
  const [actionNotes, setActionNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function load() {
    setLoading(true)
    let query = supabase
      .from('payout_requests')
      .select(`
        id, instructor_id, amount, status, requested_at, resolved_at,
        pix_key, bank_info, notes,
        instructor:instructors!payout_requests_instructor_id_fkey(
          id,
          profile:profiles!instructors_profile_id_fkey(id, full_name, avatar_url)
        )
      `)
      .order('requested_at', { ascending: false })
      .limit(100)
    if (tab === 'pending') {
      query = query.in('status', ['pending', 'processing'])
    }
    const { data } = await query
    setRequests((data ?? []) as unknown as PayoutRequest[])
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  function openAction(req: PayoutRequest, action: 'paid' | 'rejected') {
    setActionDialog({ req, action })
    setActionNotes('')
  }

  async function confirmAction() {
    if (!actionDialog) return
    const { req, action } = actionDialog
    setSubmitting(true)
    const { error } = await supabase
      .from('payout_requests')
      .update({
        status: action,
        notes: actionNotes.trim() || null,
      })
      .eq('id', req.id)
    setSubmitting(false)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success(
      action === 'paid'
        ? 'Saque marcado como pago. Instrutor será notificado.'
        : 'Solicitação rejeitada.',
    )
    setActionDialog(null)
    await load()
  }

  function statusBadge(status: PayoutRequest['status']) {
    const map: Record<PayoutRequest['status'], { label: string; cls: string }> = {
      pending:    { label: 'Pendente',    cls: 'bg-amber-100 text-amber-800 border-amber-200' },
      processing: { label: 'Processando', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
      paid:       { label: 'Pago',        cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
      rejected:   { label: 'Rejeitado',   cls: 'bg-red-100 text-red-800 border-red-200' },
    }
    const s = map[status]
    return <Badge variant="outline" className={s.cls}>{s.label}</Badge>
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4 text-teal-600" />
          Pedidos de saque (instrutores)
        </CardTitle>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTab('pending')}
            className={`text-xs px-2 py-1 rounded ${tab === 'pending' ? 'bg-teal-600 text-white' : 'text-muted-foreground hover:bg-gray-100'}`}
          >
            Pendentes
          </button>
          <button
            onClick={() => setTab('all')}
            className={`text-xs px-2 py-1 rounded ${tab === 'all' ? 'bg-teal-600 text-white' : 'text-muted-foreground hover:bg-gray-100'}`}
          >
            Todos
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={load}
            title="Recarregar"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Carregando...</div>
        ) : requests.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {tab === 'pending'
              ? 'Nenhum pedido de saque pendente.'
              : 'Nenhum pedido de saque registrado.'}
          </div>
        ) : (
          <ul className="divide-y">
            {requests.map((r) => (
              <li key={r.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={r.instructor?.profile?.avatar_url ?? undefined} />
                  <AvatarFallback className="text-xs font-semibold">
                    {getInitials(r.instructor?.profile?.full_name ?? 'I')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {r.instructor?.profile?.full_name ?? 'Instrutor'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {formatDateTime(r.requested_at)}
                    {' · '}
                    {r.pix_key ? `Pix: ${r.pix_key}` : r.bank_info ? `Banco: ${r.bank_info}` : 'Sem dados bancários'}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="font-bold text-emerald-700">{formatCurrency(r.amount)}</p>
                    {statusBadge(r.status)}
                  </div>
                  {(r.status === 'pending' || r.status === 'processing') && (
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        onClick={() => openAction(r, 'paid')}
                        className="bg-emerald-600 hover:bg-emerald-700 h-7 gap-1 text-xs"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAction(r, 'rejected')}
                        className="h-7 gap-1 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <X className="h-3.5 w-3.5" />
                        Rejeitar
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={!!actionDialog} onOpenChange={(v) => !v && setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.action === 'paid'
                ? 'Aprovar saque'
                : 'Rejeitar saque'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              {actionDialog?.action === 'paid'
                ? `Confirmar que o pagamento de ${formatCurrency(actionDialog?.req.amount ?? 0)} foi efetuado?`
                : 'Informe ao instrutor por que a solicitação está sendo rejeitada.'}
            </p>
            <Textarea
              rows={3}
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              placeholder={
                actionDialog?.action === 'paid'
                  ? 'ID da transferência ou observação interna (opcional)'
                  : 'Motivo da rejeição (será exibido ao instrutor)'
              }
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmAction}
              disabled={submitting}
              className={
                actionDialog?.action === 'paid'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-red-600 hover:bg-red-700'
              }
            >
              {submitting ? 'Salvando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
